import Vue from 'vue'
import VueRouter from 'vue-router'
import { AuthenticationService, ProfileService } from '@services'
import { useInvoiceAlerts } from '@composables'
import routes from '@routes'
import store from '@store'

Vue.use(VueRouter)

const router = new VueRouter({
  routes,
  mode: 'history',
  linkActiveClass: 'active',
  scrollBehavior: (to, __, savedPosition) => {
    if (savedPosition) return savedPosition

    if (to.hash)
      return {
        selector: to.hash
      }

    return {
      x: 0,
      y: 0
    }
  }
})

router.beforeEach(async (to, from, next) => {
  // The vue-router-user-roles library (see main.js) enforces each route's
  // meta.permissions and silently redirects a denied user — e.g. a non-
  // superuser hitting /invoicing is pushed to the Dashboard. It shows no
  // message, so stash a flag here (beforeEach runs before the library's
  // redirect) that the Dashboard reads on mount to show an access-denied
  // toast. Scoped to Invoicing to avoid firing on unrelated redirects.
  if (to.name === 'Invoicing') {
    const user = store.getters.user
    if (!user || !user.is_superuser) {
      try { window.sessionStorage.setItem('accessDeniedPage', 'invoicing') } catch (e) {}
    }
  }

  // Handle special redirect query params (BDX only)
  if (to.query['manage-bdx']) {
    const role = store.state.auth.user.role
    return role === 'Guest'
      ? next()
      : next({ name: 'ManageBDX' })
  }

  // Cash Call route protection
  if (to.meta.requiresCashCallAccess) {
    const user = store.getters.user

    if (!user || user.role === 'Guest') {
      return next({ name: 'Home' })
    }

    if (!user.can_access_cash_call) {
      return next({ name: 'Dashboard' })
    }
  }

  // Page CSS class
  document.body.className = `${(to.name || '').toLowerCase()}-page`

  next()
})


router.beforeResolve(async (to, from,  next) => {
  const isAuthenticated = store.getters.authenticated
  const user = store.getters.user

  if (isAuthenticated) {
    await ProfileService.getInfo()

    // Superuser-only invoice attention check — same "hit on every page"
    // cadence as the profile fetch above, but fired without awaiting so a
    // slow/unreachable Lincoln never delays the route transition itself.
    // The header icon / page banner just update reactively once it resolves.
    if (user.is_superuser) {
      useInvoiceAlerts().refresh()
    }

    if (!user.member_is_active) {
      Vue.toasted.show('User account is deactivated!', {
        type: 'error'
      })

      await AuthenticationService.logout()
    }

    //Cash Call enforcement
    if (to.meta.requiresCashCallAccess) {
      if (!user.can_access_cash_call) {
        Vue.toasted.show('You do not have access to Cash Call.', {
          type: 'error'
        })

        return next({ name: 'Dashboard' }) // or Home / Forbidden
      }
    }

    // Superuser-only route enforcement (e.g. /invoicing). The page and its
    // APIs are superuser-only; without this a non-superuser hitting the URL
    // directly loads a broken page (403s behind the scenes) instead of a
    // clean access-denied. Redirect to the Dashboard. Firing a toast from
    // inside the guard here is unreliable (the source page is torn down
    // mid-navigation before it renders), so we pass a flag through the
    // redirect and the Dashboard shows the message once it has mounted.
    next()
  } else {
    next()
  }
})

export default router
